import React from 'react';
import { Box, Typography } from '@mui/material';
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend,
    CartesianGrid, ReferenceArea,
} from 'recharts';
import { rolling, lastN, priorN, meanArr } from './aggregation';
import { fmtPct, fmtDelta, ROLLING, RECENT } from './format';

// Compact metric row — one strip per metric showing L7d / L14d / prior / full
// as labelled pills. Replaces the wordy text recap.
const MetricRow = ({ label, l7, l14, p14, full, delta, extra }) => {
    const Pill = ({ k, v, color }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 40 }}>
            <Typography sx={{ fontSize: 10, color: '#888', textTransform: 'uppercase' }}>{k}</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: color || 'inherit' }}>{fmtPct(v)}</Typography>
        </Box>
    );
    return (
        <Box className="sh-card sh-card-tight" sx={{ p: 1.25 }}>
            <Typography sx={{ fontSize: 11, color: '#666', fontWeight: 600, mb: 0.5 }}>{label}</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <Pill k="L7d" v={l7} />
                <Pill k="L14d" v={l14} />
                {p14 != null && <Pill k="P14d" v={p14} />}
                <Pill k="Full" v={full} />
            </Box>
            {(delta || extra) && (
                <Box sx={{ mt: 0.75, fontSize: 11, textAlign: 'center' }}>
                    {delta && <span className={`sh-${delta.cls}`}>{delta.text}</span>}
                    {extra && <span style={{ color: '#666' }}>{extra}</span>}
                </Box>
            )}
        </Box>
    );
};

const TrendSection = ({ data, agg }) => {
    const { runRate, clientNightRate, systemClean } = agg;

    const runRolling = rolling(runRate, ROLLING);
    const cnRolling = rolling(clientNightRate, ROLLING);
    const sysRolling = rolling(systemClean, ROLLING);

    // recharts wants an array of objects keyed by series.
    const chartData = data.dates.map((d, i) => ({
        date: d.slice(5),
        runDaily: runRate[i],
        run: runRolling[i],
        cn: cnRolling[i],
        sys: sysRolling[i],
    }));

    const recentStart = Math.max(0, data.dates.length - RECENT);

    const last7run = lastN(runRate, 7), last14run = lastN(runRate, 14), prior14run = priorN(runRate, 14);
    const last7cn = lastN(clientNightRate, 7), last14cn = lastN(clientNightRate, 14), prior14cn = priorN(clientNightRate, 14);
    const last7sys = lastN(systemClean, 7), last14sys = lastN(systemClean, 14);
    const cleanNightsFull = systemClean.filter((x) => x === 100).length;
    const totalNightsFull = systemClean.filter((x) => x != null && !isNaN(x)).length;
    const dRun = fmtDelta(last14run - prior14run);
    const dCn = fmtDelta(last14cn - prior14cn);

    return (
        <Box className="sh-card">
            <Typography className="sync-health-section-title" sx={{ mb: 1.5 }}>Overall trend</Typography>
            <Box sx={{ width: '100%', height: 360 }}>
                <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 12, right: 16, left: 0, bottom: 8 }}>
                        <CartesianGrid stroke="#eee" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#666' }}
                            interval={Math.max(0, Math.floor(chartData.length / 8))} />
                        <YAxis domain={[-2, 102]} ticks={[0, 20, 40, 60, 80, 100]}
                            tick={{ fontSize: 11, fill: '#666' }}
                            label={{ value: 'Success rate (%)', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#444' } }} />
                        <Tooltip
                            formatter={(v) => (v == null ? '—' : `${v.toFixed(2)}%`)}
                            contentStyle={{ fontSize: 12 }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {recentStart < chartData.length && (
                            <ReferenceArea
                                x1={chartData[recentStart].date}
                                x2={chartData[chartData.length - 1].date}
                                fill="#2a9d8f"
                                fillOpacity={0.06}
                            />
                        )}
                        <Line type="monotone" dataKey="runDaily" name="Provider-run (daily)"
                            stroke="#bbbbbb" strokeWidth={1} dot={false} connectNulls={false} opacity={0.7} />
                        <Line type="monotone" dataKey="run" name="Provider-run (7d rolling)"
                            stroke="#2a9d8f" strokeWidth={2.4} dot={false} connectNulls={false} />
                        <Line type="monotone" dataKey="cn" name="Client-night (7d rolling)"
                            stroke="#e9a23b" strokeWidth={2.2} dot={false} connectNulls={false} />
                        <Line type="monotone" dataKey="sys" name="System-night (7d rolling)"
                            stroke="#e76f51" strokeWidth={2} strokeDasharray="5 3" dot={false} connectNulls={false} />
                    </LineChart>
                </ResponsiveContainer>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1, mt: 1.5, fontSize: 12 }}>
                <MetricRow label="Provider-run" l7={last7run} l14={last14run} p14={prior14run} full={meanArr(runRate)} delta={dRun} />
                <MetricRow label="Client-night" l7={last7cn} l14={last14cn} p14={prior14cn} full={meanArr(clientNightRate)} delta={dCn} />
                <MetricRow label="System-night" l7={last7sys} l14={last14sys} full={meanArr(systemClean)} extra={`${cleanNightsFull}/${totalNightsFull} clean`} />
            </Box>
            <Typography sx={{ fontSize: 11, color: '#888', mt: 1 }}>
                Highlighted band = last {RECENT} days. Rolling window = {ROLLING} days, min_periods = 3.
            </Typography>
        </Box>
    );
};

export default TrendSection;
